import { DateTime } from 'luxon'
import { EditableFieldType, OptionType } from '@/types'
import { TaxConsultantClient } from '@/types/models'

export function classNames(...classes: ((undefined | string | false) | (undefined | string | false)[])[]): string {
    if (Array.isArray(classes[0])) {
        return classes[0].filter(Boolean).join(' ')
    }

    return classes.filter(Boolean).join(' ')
}

export function slug(
    str: string,
    separator: string = '_',
    dictionary: {
        [key: string]: string
    } = { '@': 'at' }
): string {
    let title = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

    // Convert all dashes/underscores into separator
    const flip = '-'
    title = title.replace(new RegExp('[' + flip + ']+', 'gu'), separator)

    // Replace dictionary words
    for (const [key, value] of Object.entries(dictionary)) {
        dictionary[key] = separator + value + separator
    }

    title = title.replace(new RegExp(Object.keys(dictionary).join('|'), 'gu'), (matched) => dictionary[matched])

    // Remove all characters that are not the separator, letters, numbers, or whitespace
    title = title.replace(new RegExp('[^' + separator + '\\p{L}\\p{N}\\s]+', 'gu'), '').toLowerCase()

    // Replace all separator characters and whitespace by a single separator
    title = title.replace(new RegExp('[' + separator + '\\s]+', 'gu'), separator)

    return title.trim()
}

/**
 * Add a string to an input field, taking care of the cursor position and adding spaces if necessary
 * @param string
 * @param target
 */
export function addToInput(string: string, target: HTMLInputElement | HTMLTextAreaElement): string {
    const inputValue = target.value || ''
    const start = target.selectionStart || 0
    const end = target.selectionEnd || 0
    if (inputValue.length > 0 && inputValue.substring(start - 1, start) != ' ') {
        string = ` ${string}`
    }

    // we define selectionRangeEnd here, so the cursor is not set to after the added space in case we add one after the string
    const selectionRangeEnd = start + string.length

    const cursorAtEnd = start === inputValue.length
    const followingCharacterIsSpace = inputValue.substring(end, end + 1) == ' '

    if (inputValue.length > 0 && !cursorAtEnd && !followingCharacterIsSpace) {
        string = `${string} `
    }

    const newValue = inputValue.substring(0, start) + string + inputValue.substring(end)
    target.setSelectionRange(selectionRangeEnd, selectionRangeEnd)

    return newValue
}

// --- Normalization / formatting helpers (clarity + single source of truth) ---
const toISODateSafe = (raw: string): string => {
    if (!raw) {
        return ''
    }
    const dt = DateTime.fromISO(raw)
    return dt.isValid ? dt.toISODate() || '' : ''
}

export const normalizeFormValue = (type: EditableFieldType, value: string | null): string => {
    if (value === null) {
        return ''
    }

    if (type === 'date') {
        return toISODateSafe(value)
    }

    return value
}

export function formatEditableValue(
    type: 'text' | 'textarea' | 'date' | 'number' | 'integer' | 'select' | 'currency',
    raw: string,
    options?: OptionType[],
    currency?: string
): string {
    if (raw === null) {
        return options?.length ? (options.find((o) => o.value === null)?.label ?? '') : '--'
    }

    if (raw === undefined) {
        return '--'
    }

    if (raw === '') {
        return options?.length ? (options.find((o) => o.value === '')?.label ?? '--') : '--'
    }

    if (type === 'date') {
        const dt = DateTime.fromISO(raw)
        return dt.isValid ? dt.toFormat('dd.MM.yy') : '-'
    }

    if (type === 'number') {
        const n = Number(raw)
        return Number.isFinite(n) ? formatFloat(n) : '-'
    }

    if (type === 'integer') {
        const n = parseInt(raw)
        return Number.isFinite(n) ? n.toString() : '-'
    }

    if (type === 'select' && options) {
        const option = options.find((o) => o.value === raw)
        return option ? option.label : '-'
    }

    if (type === 'currency') {
        const n = Number(raw)
        return Number.isFinite(n) ? formatCurrency(n, currency!) : '-'
    }

    return raw
}

export const formatCurrency = (amount: number | null, currency: string | null | undefined = 'EUR'): string => {
    if (amount === null || amount === undefined) {
        return '--'
    }

    if (!currency) {
        currency = 'EUR'
    }

    return parseFloat(amount.toString()).toLocaleString('de-DE', { style: 'currency', currency })
}

export const formatFloat = (amount: number): string => {
    return amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function clientHasApiIntegration(client: TaxConsultantClient, integration: string) {
    return client.api_integrations.find((apiIntegration) => apiIntegration.type === integration) !== undefined
}

export function formatDateTime(dateTime: string | null | undefined): string {
    if (!dateTime) {
        return '--'
    }

    const dt = DateTime.fromISO(dateTime)

    return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '--'
}

export function formatBytes(bytes?: number | null): string {
    if (!bytes && bytes !== 0) return '-'
    const thresh = 1024
    if (Math.abs(bytes) < thresh) {
        return bytes + ' B'
    }
    const units = ['KB', 'MB', 'GB', 'TB']
    let u = -1
    let b = bytes
    do {
        b /= thresh
        ++u
    } while (Math.abs(b) >= thresh && u < units.length - 1)
    return b.toFixed(1) + ' ' + units[u]
}

/**
 * Highlight helper: wraps occurrences of `query` within `text` in a span with bg-stone-100.
 * - Case-insensitive matching, original casing preserved in output.
 * - Returns plain text when query is empty.
 */
export function highlightMatches(text: string, query: string) {
    if (!query) {
        return text
    }

    // Escape regex special characters from query
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escaped})`, 'ig')
    const parts = text.split(regex)

    return parts.map((part, idx) => {
        if (part.toLowerCase() === query.toLowerCase()) {
            return (
                <span key={idx} className="bg-stone-400">
                    {part}
                </span>
            )
        }
        return <span key={idx}>{part}</span>
    })
}
